import { useCallback, useEffect, useRef, useState } from 'react';
import { useSurface } from '@/contexts/SurfaceContext';
import { fetchClientVersion } from '@/features/clientVersion/clientVersionApi';
import {
  nextClientVersionState,
  readClientVersion,
  writeClientVersion,
} from '@/features/clientVersion/clientVersionStorage';

/**
 * Mobile only. Each client-version response is compared with the saved number, then saved.
 * No saved number yet: store the API number and do not show the dialog.
 * A newer API number shows the dialog and replaces the saved number.
 * A null or failed response leaves storage unchanged and hides the dialog.
 */
export function useClientVersionNotice(): {
  visible: boolean;
  updatedAt: string | null;
  dismiss: () => void;
} {
  const { surface } = useSurface();
  const [visible, setVisible] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const dismissed = useRef(false);

  useEffect(() => {
    if (surface !== 'mobile') {
      setVisible(false);
      setUpdatedAt(null);
      return;
    }

    let cancelled = false;
    dismissed.current = false;
    const stored = readClientVersion();

    fetchClientVersion()
      .then((remote) => {
        if (cancelled) return;
        const next = nextClientVersionState(stored, remote?.version ?? null);
        if (next.persist != null) writeClientVersion(next.persist);
        if (dismissed.current) return;
        setUpdatedAt(next.show ? remote?.updatedAt ?? null : null);
        setVisible(next.show);
      })
      .catch(() => {
        /* no number to compare; leave the dialog hidden and storage unchanged */
      });

    return () => {
      cancelled = true;
    };
  }, [surface]);

  const dismiss = useCallback(() => {
    dismissed.current = true;
    setUpdatedAt(null);
    setVisible(false);
  }, []);

  return { visible, updatedAt, dismiss };
}
